import { useEffect, useState } from 'react'
import { Trans } from 'react-i18next'

import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, Modal } from '@makyoapp/makyo-ui'

import { SyncDetailPagesState } from '@/hooks/useSyncDetailPages'

const IconStatusMap = {
  idle: <MaterialIcon icon='mode_standby' size={16} className='text-light' />,
  processing: <MaterialIcon icon='directory_sync' size={16} className='animate-spin' />,
  finished: <MaterialIcon icon='check_circle' size={16} className='text-brand-500' />,
  error: <MaterialIcon icon='error' size={16} className='text-red-500' />,
}

/**
 * Details Page Sync Modal
 *
 * @note only available in app builder (window.appMode === 'preview')
 */
export const DetailsPageSyncModal = ({
  syncState,
  onFinish,
}: {
  syncState: SyncDetailPagesState
  onFinish: () => void
}) => {
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    setShowModal(syncState.status !== 'idle')
  }, [syncState.status])

  const handleClose = () => {
    if (syncState.status === 'processing') return
    setShowModal(false)
  }

  const handleFinish = () => {
    onFinish()
    handleClose()
  }

  const addTotal = syncState.addStatus.total
  const deleteTotal = syncState.deleteStatus.total
  const totalPages = addTotal + deleteTotal
  const hasChanges = totalPages > 0

  const getErrorCount = () => {
    return syncState.deleteStatus.errors + syncState.addStatus.errors
  }

  return (
    <Modal fix show={showModal} onHide={handleClose} closeOnClickOutside={false}>
      <Modal.Header onCloseClick={handleClose}>
        <div className='py-2 flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <span>🧹</span>
            <Trans>Screen Housekeeping</Trans>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body className='modal-sm relative text-m-m' noPadding>
        <div className='px-4 py-4 space-y-6'>
          <div className='text-m-s font-normal text-light'>
            <Trans>Optimizing and refreshing your setup</Trans>
          </div>

          {/* No changes state */}
          {!hasChanges && syncState.status === 'finished' && (
            <div className='flex flex-col gap-3 text-center justify-center py-4'>
              <div className='text-xl'>🎉</div>
              <div className='space-y-1'>
                <div className='text-m-m font-semibold'>
                  <Trans>Housekeeping complete</Trans>
                </div>
                <div className='text-m-s text-light flex items-center justify-center gap-1.5'>
                  <MaterialIcon icon='check' size={16} className='text-brand-500' />
                  <Trans>Everything is up to date and optimized</Trans>
                </div>
              </div>
            </div>
          )}

          {/* Progress section */}
          {hasChanges && syncState.status === 'processing' && (
            <>
              <div className='space-y-3'>
                <div className='flex items-center justify-between'>
                  <div className='font-semibold'>
                    <Trans>Progress</Trans>
                  </div>
                  <div className='text-m-s tabular-nums'>
                    {Math.round(((syncState.deleteStatus.current + syncState.addStatus.current) / totalPages) * 100)}%
                  </div>
                </div>
                <div className='w-full h-2 bg-zync-200 dark:bg-slate-600 rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-brand-500 rounded-full transition-all duration-300 ease-out'
                    style={{
                      width: `${((syncState.deleteStatus.current + syncState.addStatus.current) / totalPages) * 100}%`,
                    }}
                  />
                </div>
              </div>

              {/* Summary during processing */}
              <div className='space-y-2'>
                <div className='font-semibold'>
                  <Trans>Summary</Trans>
                </div>
                <div className='space-y-1.5 text-m-m text-light'>
                  {deleteTotal > 0 && (
                    <div className='flex items-center gap-2'>
                      {IconStatusMap[syncState.deleteStatus.status]}
                      <span>
                        <Trans>Cleaning up outdated items</Trans> ({syncState.deleteStatus.current}/{deleteTotal})
                      </span>
                    </div>
                  )}
                  {addTotal > 0 && (
                    <div className='flex items-center gap-2'>
                      {IconStatusMap[syncState.addStatus.status]}
                      <span>
                        <Trans>Setting up new items</Trans> ({syncState.addStatus.current}/{addTotal})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Completed state with changes */}
          {hasChanges && (syncState.status === 'finished' || syncState.status === 'error') && (
            <>
              <div className='space-y-3'>
                <div className='flex items-center justify-between'>
                  <div className='font-semibold'>
                    <Trans>Progress</Trans>
                  </div>
                  <div className='text-m-s tabular-nums'>100%</div>
                </div>
                <div className='w-full h-2 bg-zync-200 dark:bg-slate-600 rounded-full overflow-hidden'>
                  <div className='h-full bg-brand-500 rounded-full transition-all duration-300 ease-out w-full' />
                </div>
              </div>

              {/* Summary */}
              <div className='space-y-2'>
                <div className='font-semibold'>
                  <Trans>Summary</Trans>
                </div>
                <div className='space-y-1.5 text-m-m'>
                  <div className='flex items-center gap-2'>
                    <span>🎉</span>
                    <Trans>Housekeeping complete</Trans>
                  </div>
                  {syncState.status === 'finished' && getErrorCount() === 0 ? (
                    <div className='flex items-center gap-2 text-light'>
                      <MaterialIcon icon='check' size={16} className='text-brand-500' />
                      <Trans>Everything is up to date and optimized</Trans>
                    </div>
                  ) : (
                    <div className='flex items-center gap-2 text-orange-600 dark:text-orange-400'>
                      <MaterialIcon icon='warning' size={16} />
                      <span>
                        {getErrorCount()} {getErrorCount() === 1 ? 'item is' : 'items are'} pending optimization
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Warning message during processing */}
          {syncState.status === 'processing' && (
            <div className='flex items-start gap-2 text-orange-600 dark:text-orange-400 text-m-s bg-orange-50 dark:bg-orange-950/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800/30'>
              <MaterialIcon icon='info' size={16} className='mt-0.5 flex-shrink-0' />
              <span>
                <Trans>Please wait while we optimize your screens</Trans>
              </span>
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className='justify-between'>
        <div></div>

        <Button onClick={handleFinish} disabled={syncState.status === 'processing'}>
          <Trans>Ok</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
